import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { contact } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [searchParams] = useSearchParams();
  
  // Récupération de la catégorie dans l'URL (?category=...)
  const categoryUrl = searchParams.get('category');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    category: 'fremyc',
    subject: 'devis',
    activity :'',
    message: ''
  });

  useEffect(() => {
    const categoryParam = searchParams.get('category');
    const stayParam = searchParams.get('stay');

    const stayMapping = {
      'famille': 'Séjour Famille',
      'rando': 'Séjour Randonnée',
      'vtt': 'Séjour VTT',
      'autre': 'Autre'
    };

    if (categoryParam || stayParam) {
      setFormData(prev => ({
        ...prev,
        category: categoryParam || prev.category,
        stay_type: stayMapping[stayParam] || ''
      }));
    }
  }, [searchParams]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast.success('Message envoyé !', {
          description: response.data.message,
          duration: 5000,
        });
        
        setFormData({ 
          name: '', 
          email: '', 
          phone: '', 
          category: categoryUrl || 'fremyc',
          subject: 'devis', 
          activity :'',
          message: '' 
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Erreur', {
        description: error.response?.data?.detail || 'Une erreur est survenue lors de l\'envoi de votre message. Veuillez réessayer.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/terrasse1.jpg')"  }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Contactez-nous </h1>
          <p className="text-xl md:text-2xl text-gray-200 leading-relaxed">
            Besoin d'un devis personnalisé ? Une question en lien avec les activités ?
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-8 text-gray-900">Envoyez-nous un message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                
                {/* On n'affiche les radios que s'il n'y a PAS de paramètre category dans l'URL */}
                {!categoryUrl && (
                  <div className="bg-gray-50 rounded-xl p-6 border-2 border-gray-200">
                    <Label htmlFor="category" className="text-base font-semibold mb-3 block">Votre demande concerne *</Label>
                    <div className="grid md:grid-cols-3 gap-4">
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.category === 'fremyc' 
                          ? 'border-[#afab53] bg-[#afab53]/10' 
                          : 'border-gray-300 bg-white hover:border-[#afab53]/50'
                      }`}>
                        <input
                          type="radio"
                          name="category"
                          value="fremyc"
                          checked={formData.category === 'fremyc'}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#afab53] focus:ring-[#afab53]"
                        />
                        <div>
                          <div className="font-semibold text-gray-900">Fremyc</div>
                          <div className="text-sm text-gray-600">Activités</div>
                        </div>
                      </label>
                      
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.category === 'pegue' 
                          ? 'border-[#afab53] bg-[#afab53]/10' 
                          : 'border-gray-300 bg-white hover:border-[#afab53]/50'
                      }`}>
                        <input
                          type="radio"
                          name="category"
                          value="pegue"
                          checked={formData.category === 'pegue'}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#afab53] focus:ring-[#afab53]"
                        />
                        <div>
                          <div className="font-semibold text-gray-900">La Pègue</div>
                          <div className="text-sm text-gray-600">Camping</div>
                        </div>
                      </label>
                      
                      <label className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.category === 'draille' 
                          ? 'border-[#afab53] bg-[#afab53]/10' 
                          : 'border-gray-300 bg-white hover:border-[#afab53]/50'
                      }`}>
                        <input
                          type="radio"
                          name="category"
                          value="draille"
                          checked={formData.category === 'draille'}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#afab53] focus:ring-[#afab53]"
                        />
                        <div>
                          <div className="font-semibold text-gray-900">La Draille</div>
                          <div className="text-sm text-gray-600">Gîte</div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}

                {/* Champ Séjour (uniquement pour le Gîte) */}
                {formData.category === 'draille' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Type de séjour (facultatif)</label>
                    <select
                      name="stay_type"
                      value={formData.stay_type || ''}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-md bg-white"
                    >
                      <option value="">Choisir un séjour</option>
                      <option value="Séjour Famille">Séjour Famille</option>
                      <option value="Séjour Randonnée">Séjour Randonnée</option>
                      <option value="Séjour VTT">Séjour VTT</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                )}

                {formData.category === 'fremyc' && (
                  <div>
                    <Label htmlFor="activity">Activité concernée *</Label>
                    <select
                      id="activity"
                      name="activity"
                      required
                      value={formData.activity}
                      onChange={handleChange}
                      className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#afab53] focus:border-transparent"
                    >
                      <option value=""> Sélectionnez une activité</option>
                      <option value="canyoning">Canyoning</option>
                      <option value="via-ferrata">Via ferrata</option>
                      <option value="escalade">Escalade</option>
                      <option value="speleologie">Spéléologie</option>
                      <option value="canoe-kayak">Canoë Kayak</option>
                      <option value="vtt">VTT</option>
                      <option value="trottinette">Trottinette tout terrain</option>
                      <option value="tir-arc">Tir à l’arc</option>
                      <option value="randonnee">Randonnée</option>
                      <option value="autre">Autre activité</option>
                    </select>
                  </div>
                )}

                <div>
                  <Label htmlFor="name">Nom complet *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Votre nom"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="votre.email@exemple.com"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="phone">Téléphone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="06 12 34 56 78"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="subject">Sujet *</Label>
                  <select
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    className="mt-2 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#afab53] focus:border-transparent"
                  >
                    <option value="devis">Demande de devis</option>
                    <option value="reservation">Réservation</option>
                    <option value="information">Demande d'information</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Votre message..."
                    rows={6}
                    className="mt-2"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full bg-[#afab53] hover:bg-[#9a9648] text-white text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Envoi...</>
                  ) : (
                    <><Send className="w-5 h-5 mr-2" />Envoyer le message</>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Info - Affichage filtré par le paramètre URL */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-8 text-gray-900">Nos coordonnées</h2>
              </div>

              {/* La Draille : affiché si pas de catégorie OU si category=draille */}
              {(!categoryUrl || categoryUrl === 'draille') && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Gîte La Draille</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#afab53] flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{contact.adresses.draille}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#afab53] flex-shrink-0" />
                      <div className="text-gray-700">
                        <p>{contact.phones.draille.fixe}</p>
                        <p>{contact.phones.draille.mobile}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Frémyc : affiché si pas de catégorie OU si category=fremyc */}
              {(!categoryUrl || categoryUrl === 'fremyc') && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Activités Fremyc</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#afab53] flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{contact.adresses.fremyc}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#afab53] flex-shrink-0" />
                      <div className="text-gray-700">
                        <p>{contact.phones.fremyc.mobile1}</p>
                        <p>{contact.phones.fremyc.mobile2}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* La Pègue : affiché si pas de catégorie OU si category=pegue */}
              {(!categoryUrl || categoryUrl === 'pegue') && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-4 text-gray-900">Camping La Pègue</h3>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-[#afab53] flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{contact.adresses.pegue}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-[#afab53] flex-shrink-0" />
                      <span className="text-gray-700">{contact.phones.pegue.mobile}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-gradient-to-br from-[#afab53] to-[#8a8742] rounded-xl p-6 text-white">
                <h3 className="text-xl font-bold mb-3">Email</h3>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 flex-shrink-0" />
                  <a href={`mailto:${contact.email}`} className="hover:underline">
                    {contact.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 text-center">Nous trouver</h2>
          <div className="bg-white rounded-2xl overflow-hidden shadow-2xl h-[500px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2861.439396264906!2d3.4244223!3d44.1793777!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12b39f375f190e3f%3A0x7d6c6e755254a678!2sCamping%20La%20P%C3%A8gue!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              title="Localisation Meyrueis"
            ></iframe>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;